import {
  BebopperIcon,
  OgAchievoorIcon,
  OgCommunityCallFrenIcon,
  OgContributoorIcon,
  OgOverachievoorIcon,
  OgTestoorIcon,
  OgTradoorIcon,
  PinHoldoorIcon,
  QuestoorIcon,
  TwitterMaxiIcon,
} from "components/Icons";
import React from "react";
import { Address, Hash } from "viem";
import { Chain, mainnet } from "viem/chains";
import { Amount } from "../types/amount";
import { SUPPORTED_CHAINS } from "config/chains";

export const PROJECT_NAME = "Bebop";

export enum BEBOP_ROUTES {
  Home = "/",
  Trade = "/trade",
  Activity = "/activity",
  PageNotFound = "/not-found",
  Wildcard = "*",
}

export const MAX_SELECTABLE_TOKENS = 5;
export const NATIVE_TOKENS = ["ETH", "BTC", "MATIC"];
export const QUOTE_REFETCH_INTERVAL_MS = 10_000;
export const BALANCE_REFETCH_INTERVAL_MS = 30_000;
export const TOKEN_REFETCH_INTERVAL_MS = 30_000;
export const FETCH_BALANCE_RETRY_TIMES = 3;
export const FETCH_TOKENS_RETRY_TRIES = 3;
export const USER_INPUT_DEBOUNCE_TIME_MS = 250;
export const DIGITS_TO_SHOW_ON_BALANCE = 5;
export const DIGITS_TO_SHOW_ON_CONFIRM = 5;
export const QUOTE_DEBOUNCE_TIME = 1000;

export const SEPARATOR_DOT = "•";
export const BEBOP_GUILD_ID = 11177;
export const GUILD_NAMES_TO_ID = {
  OG_CONTRIBUTOOR: 23153,
  CONTRIBUTOOR: 43738,
  OG_OVERACHIEVOOR: 21322,
  OG_TRADOOR: 21320,
  OG_ACHIEVOOR: 21321,
  OG_TESTOOR: 21319,
  OG_COMMUNITY_CALL_FREN: 17064,
  PIN_HOLDOOR: 56300,
  QUESTOOR: 28281,
  TWITTER_MAXI: 17063,
  BEBOPPER: 56299,
  TRADOOR: 61741,
  DEV_MOCK: 99999,
};
export const GUILD_ROLES_PRECEDENCE: Record<number, number> = [
  GUILD_NAMES_TO_ID.OG_CONTRIBUTOOR,
  GUILD_NAMES_TO_ID.OG_ACHIEVOOR,
  GUILD_NAMES_TO_ID.OG_TRADOOR,
  GUILD_NAMES_TO_ID.OG_ACHIEVOOR,
  GUILD_NAMES_TO_ID.OG_TESTOOR,
  GUILD_NAMES_TO_ID.TRADOOR,
  GUILD_NAMES_TO_ID.PIN_HOLDOOR,
  GUILD_NAMES_TO_ID.QUESTOOR,
  GUILD_NAMES_TO_ID.OG_COMMUNITY_CALL_FREN,
  GUILD_NAMES_TO_ID.TWITTER_MAXI,
  GUILD_NAMES_TO_ID.BEBOPPER,
].reduce((prev, curr, idx) => {
  return {
    ...prev,
    [curr]: idx,
  };
}, {});

export const GUILD_ROLES_EMOJIS: Record<number, React.FC<React.SVGProps<SVGSVGElement>>> = {
  [GUILD_NAMES_TO_ID.OG_CONTRIBUTOOR]: OgContributoorIcon,
  [GUILD_NAMES_TO_ID.OG_OVERACHIEVOOR]: OgOverachievoorIcon,
  [GUILD_NAMES_TO_ID.OG_TRADOOR]: OgTradoorIcon,
  [GUILD_NAMES_TO_ID.OG_ACHIEVOOR]: OgAchievoorIcon,
  [GUILD_NAMES_TO_ID.OG_TESTOOR]: OgTestoorIcon,
  [GUILD_NAMES_TO_ID.QUESTOOR]: QuestoorIcon,
  [GUILD_NAMES_TO_ID.PIN_HOLDOOR]: PinHoldoorIcon,
  [GUILD_NAMES_TO_ID.TWITTER_MAXI]: TwitterMaxiIcon,
  [GUILD_NAMES_TO_ID.BEBOPPER]: BebopperIcon,
  [GUILD_NAMES_TO_ID.TRADOOR]: OgTradoorIcon,
  [GUILD_NAMES_TO_ID.OG_COMMUNITY_CALL_FREN]: OgCommunityCallFrenIcon,
  [GUILD_NAMES_TO_ID.DEV_MOCK]: BebopperIcon,
};

export const DUMMY_WALLET = "0x0000000000000000000000000000000000000001" as Hash;

export const GAS_ESTIMATE_MULTIPLIER = BigInt(2);

export const TWITTER_IMAGE_URL = "https://twitter.com/";
export const TWITTER_IMAGE_URLS = [
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206635948675075/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206432894222336/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206386643443712/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206339839365120/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206251897421824/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206201741930496/photo/1`,
];

export const TIME_BEFORE_USER_IS_IDLE = 60_000;

export const FALLBACK_DISPLAY_DECIMALS = 5;

export const NAV_LINKS = {
  BEBOP: "https://bebop.xyz",
  DISCORD: "https://discord.com/invite/bebop",
  TWITTER: "https://twitter.com/bebop_dex",
  MEDIUM: "https://medium.com/bebop-dex",
  BLOG: "https://medium.com/bebop-dex",
  FAQ: "https://help.bebop.xyz/en",
  FAQ_DEVELOPERS: "https://help.bebop.xyz/en/collections/3939904-bebop-api",
  JOBS: "https://jobs.lever.co/Bebop",
  EMAIL: "mailto:hello@bebop.xyz",
  EMAIL_DEVELOPERS: "mailto:api@bebop.xyz",
  BRAND: "https://docs.bebop.xyz/bebop/brand-guidance/logos",
  TERMS_AND_CONDITIONS: "/terms.pdf",
  PRIVACY_POLICY: "/privacy.pdf",
  DOCUMENTATION: "https://docs.bebop.xyz",
  DOCUMENTATION_BEBOP_INTRODUCTION: "https://docs.bebop.xyz/bebop",
  DOCUMENTATION_API_INTRODUCTION: "https://docs.bebop.xyz/bebop/bebop-api/api-introduction",
  BADGES: "https://bebop.rep3.gg/",
  GUILD: "https://guild.xyz/bebop",

  WINTERMUTE: "https://www.wintermute.com",

  FORM_MARKET_MAKERS: "https://survey.typeform.com/to/oNtFKFm3",
  FORM_API_INTEGRATION: "https://survey.typeform.com/to/QdsBd1bp",
  FORM_SOLVERS: "https://survey.typeform.com/to/zZVtZdId",
  FORM_REACH_OUT: "https://survey.typeform.com/to/oy4NaTbn",

  AUDIT_REPORT_MIXBYTES: "https://github.com/mixbytes/audits_public/tree/master/Bebop",
  AUDIT_REPORT_PESSIMISTIC:
    "https://github.com/pessimistic-io/audits/blob/eb05b29457fc966865e91e9e905f58b4e8e7679b/Bebop%20Security%20Analysis%20by%20Pessimistic.pdf",
  AUDIT_REPORT_DECURITY:
    "https://github.com/Decurity/audits/blob/master/Bebop/bebop-jam-audit-report-1.1.pdf",

  DOCUMENTATION_MANAGE_PERMISSIONS:
    "https://help.bebop.xyz/en/articles/6499751-why-do-i-need-to-manage-permissions-and-approve-the-use-of-tokens",
  TOKENS_THAT_CAN_BE_TRADED:
    "https://help.bebop.xyz/en/articles/6499728-what-tokens-can-i-trade-on-bebop",
};

export const SOCIAL_CTA_LINKS = {
  SHARE_TWEET_PREFIX: "https://twitter.com/intent/tweet?text=",
};

export const ACCORDIONS = {
  SWAPANDSEND: "swap-and-send",
  DETAILS: "trade-details",
} as const;

export const PERMIT_TWO_CONTRACT_ADDRESS = "0x000000000022d473030f116ddee9f6b43ac78ba3";
export const MAX_UINT_256 = BigInt(
  "115792089237316195423570985008687907853269984665640564039457584007913129639935"
);
export const MAX_UINT_160 = BigInt("1461501637330902918203684832716283019655932542975");

export const USDT_CONTRACTS = {
  [mainnet.id]: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
};

export const OMG_CONTRACTS = {
  [mainnet.id]: "0xd26114cd6EE289AccF82350c8d8487fedB8A0C07",
};

export const ALCHEMY_API_KEY = "GjpKGlKJy4WtofaSCs_5_B7NZSX8K0vw";
export const ZERION_NFT_CONTRACT = "0x932261f9fc8da46c4a22e31b45c4de60623848bf";

export const DEFAULT_DECIMAL_SEPARATOR = ".";

export const SENTRY_CONTEXTS = {
  APPROVALS: "approvals",
  BALANCES: "balances",
  ALLOWANCES: "allowances",
};

export const enum Filter {
  Network = "network",
  Transaction = "transaction",
  Token = "token",
  Amount = "amount",
  Date = "date",
}

export const enum TransactionType {
  Trade = "Trade",
  TradeAndSend = "Trade and send",
}

export interface TransactionTypeProps {
  icon: React.ReactNode;
}

export interface IAmountFilter {
  min: Amount | undefined;
  max: Amount | undefined;
}

export interface FiltersInterface {
  [Filter.Network]: Array<Chain["name"]> | undefined;
  [Filter.Transaction]: Array<TransactionType> | undefined;
  [Filter.Token]: Array<Hash> | undefined;
  [Filter.Amount]: IAmountFilter | undefined;
  [Filter.Date]: Array<string> | undefined;
}

export const FilterOptions = {
  Network: [SUPPORTED_CHAINS.map(network => network.name)],
  Transaction: ["Trade", "Approval", "Revoke", "Wrapping", "Unwrapping"],
  Token: [], //TODO TOKEN: what?
  Date: ["All", "1 Day", "1 Week", "1 Month", "3 Months", "6 Months", "12 Months"],
};

export const NUM_TRADES_TO_FETCH = 500;

//TODO TOKEN: Do we need The addresses for networks other than ethereum?
export const DAI_ETHEREUM_ADDRESS = "0x6b175474e89094c44da98b954eedeac495271d0f";
const RENBTC_ETHEREUM_ADDRESS = "0xeb4c2781e4eba804ce9a9803c67d0893436bb27d";
export const DAI_LIKE_PERMIT1_TOKENS = [DAI_ETHEREUM_ADDRESS, RENBTC_ETHEREUM_ADDRESS];
export const LDO_ETHEREUM_ADDRESS = "0x5a98fcbea516cf06857215779fd812ca3bef1b32";
const CRV_ETEHEREUM_ADDRESS = "0xD533a949740bb3306d119CC777fa900bA034cd52";

/**
 * Some tokens require a revoke before the increasing allowances/approvals
 */
export const REVOKE_BEFORE_APPROVAL_REQUIRED = [
  CRV_ETEHEREUM_ADDRESS,
  LDO_ETHEREUM_ADDRESS,
] as Array<Hash>;

export const JAM_CONTRACT = "0xbEbEbEb035351f58602E0C1C8B59ECBfF5d5f47b";
export const PMM_CONTRACT = "0xBeB09000fa59627dc02Bb55448AC1893EAa501A5";
export const JAM_BALANCE_MANAGER = "0xfE96910cF84318d1B8a5e2a6962774711467C0be";

export const VALID_APPROVAL_SPENDERS = [PMM_CONTRACT, JAM_BALANCE_MANAGER];

export const NATIVE_TOKEN_ADDRESS: Address = "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE";

export const WRAPPED_ABI = [
  {
    constant: true,
    inputs: [],
    name: "name",
    outputs: [{ name: "", type: "string" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: false,
    inputs: [
      { name: "guy", type: "address" },
      { name: "wad", type: "uint256" },
    ],
    name: "approve",
    outputs: [{ name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    constant: true,
    inputs: [],
    name: "totalSupply",
    outputs: [{ name: "", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: false,
    inputs: [
      { name: "src", type: "address" },
      { name: "dst", type: "address" },
      {
        name: "wad",
        type: "uint256",
      },
    ],
    name: "transferFrom",
    outputs: [{ name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    constant: false,
    inputs: [{ name: "wad", type: "uint256" }],
    name: "withdraw",
    outputs: [],
    payable: false,
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    constant: true,
    inputs: [],
    name: "decimals",
    outputs: [{ name: "", type: "uint8" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: true,
    inputs: [{ name: "", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: true,
    inputs: [],
    name: "symbol",
    outputs: [{ name: "", type: "string" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: false,
    inputs: [
      { name: "dst", type: "address" },
      { name: "wad", type: "uint256" },
    ],
    name: "transfer",
    outputs: [{ name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    constant: false,
    inputs: [],
    name: "deposit",
    outputs: [],
    payable: true,
    stateMutability: "payable",
    type: "function",
  },
  {
    constant: true,
    inputs: [
      { name: "", type: "address" },
      { name: "", type: "address" },
    ],
    name: "allowance",
    outputs: [{ name: "", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  { payable: true, stateMutability: "payable", type: "fallback" },
  {
    anonymous: false,
    inputs: [
      { indexed: true, name: "src", type: "address" },
      {
        indexed: true,
        name: "guy",
        type: "address",
      },
      { indexed: false, name: "wad", type: "uint256" },
    ],
    name: "Approval",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, name: "src", type: "address" },
      {
        indexed: true,
        name: "dst",
        type: "address",
      },
      { indexed: false, name: "wad", type: "uint256" },
    ],
    name: "Transfer",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, name: "dst", type: "address" },
      {
        indexed: false,
        name: "wad",
        type: "uint256",
      },
    ],
    name: "Deposit",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, name: "src", type: "address" },
      {
        indexed: false,
        name: "wad",
        type: "uint256",
      },
    ],
    name: "Withdrawal",
    type: "event",
  },
] as const;

// For framer-motion
export const SPRING_ANIMATION = { type: "spring", damping: 30, mass: 1, stiffness: 400 };

// REMOVE when we move to indexing by contract address
export const BRIDGED_USDC_ARB_ADDRESS = "0xFF970A61A04b1cA14834A43f5dE4533eBDDB5CC8";

export const Z_INDICES = {
  BACKGROUND: 0,
  CONTENT_BASE: 1000,
  CONTENT_SURFACE: 5000,
  NAV_BAR: 10_000,
  POP_UPS: 100_000,
  SOCIALS: 100_000,
  DRAWER: 1_000_000_000_000,
};

export const AMOUNT_TO_SHOW_TRADE_SURPLUS = 1;
export const SUBSCRIPT_AMOUNT_DECIMALS = 2;
